"use client";

import React from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { leftIcon, rightIcon, showPasswordToggle, type, className, ...props },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const isPassword = type === "password";
    const usePasswordToggle = isPassword && showPasswordToggle;
    const inputType = usePasswordToggle
      ? showPassword
        ? "text"
        : "password"
      : type;

    const toggleIcon = usePasswordToggle ? (
      <button
        type="button"
        onClick={() => setShowPassword((p) => !p)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2A2A] hover:opacity-70 cursor-pointer"
        tabIndex={-1}
        aria-label={showPassword ? "Hide password" : "Show password"}
      >
        {showPassword ? (
          <EyeSlash className="h-5 w-5" weight="regular" />
        ) : (
          <Eye className="h-5 w-5" weight="regular" />
        )}
      </button>
    ) : null;

    return (
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
            {leftIcon}
          </div>
        )}

        <input
          ref={ref}
          type={inputType}
          className={cn(
            "w-full rounded-xl bg-[var(--gray-50)] px-4 py-3 placeholder:text-[var(--gray-500)] text-sm outline-none transition border border-transparent",
            "focus:border-blue-500 focus:bg-white",
            leftIcon && "pl-10",
            (rightIcon || usePasswordToggle) && "pr-10",
            className
          )}
          {...props}
        />

        {usePasswordToggle ? toggleIcon : rightIcon ? (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
            {rightIcon}
          </div>
        ) : null}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;